/**
 * Google Sign-In Button
 *
 * Renders the official Google Identity Services button and hands the
 * credential (ID token) to the caller. Renders NOTHING when
 * VITE_GOOGLE_CLIENT_ID is not configured — the feature is simply off, so
 * environments without an OAuth client (local dev, CI) show no dead button.
 */

import { useEffect, useRef, useState, useCallback } from 'react';

// Minimal typing for the GIS script we load on demand
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
          }) => void;
          renderButton: (
            element: HTMLElement,
            options: {
              theme?: 'outline' | 'filled_blue' | 'filled_black';
              size?: 'large' | 'medium' | 'small';
              text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
              shape?: 'rectangular' | 'pill';
              width?: number;
            }
          ) => void;
        };
      };
    };
  }
}

const GSI_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

interface GoogleSignInButtonProps {
  /** Receives the Google ID token when the user completes sign-in. */
  onCredential: (idToken: string) => void | Promise<void>;
  /** GIS button text variant (default "signin_with"). */
  text?: 'signin_with' | 'continue_with';
}

export default function GoogleSignInButton({ onCredential, text = 'signin_with' }: GoogleSignInButtonProps) {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
  const buttonRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);
  const [scriptReady, setScriptReady] = useState<boolean>(() => !!window.google);

  const handleCredential = useCallback(
    (response: { credential: string }) => {
      void onCredential(response.credential);
    },
    [onCredential]
  );

  // Load the GIS script once
  useEffect(() => {
    if (!clientId || scriptReady) return;
    const existing = document.querySelector(`script[src="${GSI_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => setScriptReady(true), { once: true });
      if (window.google) setScriptReady(true);
      return;
    }
    const script = document.createElement('script');
    script.src = GSI_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => setScriptReady(true);
    document.body.appendChild(script);
  }, [clientId, scriptReady]);

  // Initialize + render the button when everything is ready
  useEffect(() => {
    if (!clientId || !scriptReady || initializedRef.current) return;
    if (!window.google || !buttonRef.current) return;

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: handleCredential,
    });
    window.google.accounts.id.renderButton(buttonRef.current, {
      theme: 'outline',
      size: 'large',
      text,
      shape: 'rectangular',
      width: 280,
    });
    initializedRef.current = true;
  }, [clientId, scriptReady, handleCredential, text]);

  if (!clientId) return null;

  return (
    <div>
      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="bg-white px-2 text-gray-500">or</span>
        </div>
      </div>
      <div className="flex justify-center" ref={buttonRef} />
    </div>
  );
}
